import { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Alert, CircularProgress, Link } from "@mui/material";
import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    PhoneAuthProvider,
    signInWithCredential,
} from "firebase/auth";
import { auth } from "../utils/firebase";
import thanq from "../api/thanq";
import useCountdown from "../hooks/useCountdown";
// Define OTP states similar to user web
const OTPStates = {
    notInitiated: "NOT_INITIATED",
    sending: "SENDING",
    sent: "SENT",
    failed: "FAILED",
    replied: "REPLIED",
    incorrect: "INCORRECT",
    verified: "VERIFIED",
};

const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^[1-9]\d{9}$/; 
    return phoneRegex.test(phone);
};

const Login = () => {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [otpState, setOtpState] = useState(OTPStates.notInitiated);
    const [verificationId, setVerificationId] = useState(null);
    const [generalError, setGeneralError] = useState("");

    const { countdown, startCountdown, resetCountdown } = useCountdown({
        seconds: 45,
    });

    useEffect(() => {
        setupRecaptcha();
        return () => {
            window.recaptchaVerifier = null;
            const elem = document.getElementById("recaptcha-container");
            if (elem) {
                elem.innerHTML = "";
            }
        };
    }, []);

    useEffect(() => {
        if (otpState === OTPStates.sent) {
            startCountdown();
        }
    }, [otpState, startCountdown]);

    const setupRecaptcha = () => {
        try {
            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
                    size: "invisible",
                });
            }
        } catch (error) {
            console.error("Recaptcha setup error:", error);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();

        // Validate phone number
        if (!isValidPhoneNumber(phone)) {
            setPhoneError("Please enter a valid 10-digit phone number");
            return;
        }

        setPhoneError("");
        setGeneralError("");

        // Check if admin exists
        try {
            await thanq.get(`admin/auth/${phone}`);
        } catch (err) {
            setPhoneError("No corresponding company found");
            return;
        }

        // Send OTP
        await sendOTP();
    };

    const sendOTP = async () => {
        try {
            setupRecaptcha();
            setOtp("");
            resetCountdown();
            setOtpState(OTPStates.sending);

            const appVerifier = window.recaptchaVerifier;
            const fullPhone = `+91${phone}`;
            const confirmationResult = await signInWithPhoneNumber(auth, fullPhone, appVerifier);

            setVerificationId(confirmationResult.verificationId);
            setOtpState(OTPStates.sent);
        } catch (error) {
            console.error("Send OTP error:", error);
            setGeneralError("Failed to send OTP. Please try again.");
            setOtpState(OTPStates.failed);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();

        if (otp.length !== 6) {
            return;
        }

        try {
            resetCountdown();
            setOtpState(OTPStates.replied);
            setGeneralError("");

            const credential = PhoneAuthProvider.credential(verificationId, otp);
            const verified = await signInWithCredential(auth, credential);

            if (verified) {
                setOtpState(OTPStates.verified);
            } else {
                setOtp("");
                setOtpState(OTPStates.incorrect);
            }
        } catch (error) {
            console.error("Verify OTP error:", error);
            setOtp("");
            setOtpState(OTPStates.incorrect);
        }
    };

    const handleReset = () => {
        setPhone("");
        setOtp("");
        setPhoneError("");
        setGeneralError("");
        setVerificationId("");
        setOtpState(OTPStates.notInitiated);
        resetCountdown();
    };

    const handleResendOtp = () => {
        if (countdown === 0) {
            sendOTP();
        }
    };

    const isLoading = [OTPStates.sending, OTPStates.replied, OTPStates.verified].includes(otpState);

    const showOtpField = [OTPStates.sent, OTPStates.incorrect].includes(otpState);

    return (
        <Box
            p={3}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            maxWidth={400}
            mx="auto"
        >
            <Typography variant="h4" gutterBottom>
                Admin Login
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2} textAlign="center">
                Enter your registered phone number to receive an OTP
            </Typography>

            {/* General Error */}
            {generalError && (
                <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
                    {generalError}
                </Alert>
            )}

            {/* Phone Number Field */}
            <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={phone}
                onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // Only digits
                    if (value.length <= 10) {
                        setPhone(value);
                    }
                }}
                margin="normal"
                required
                disabled={otpState !== OTPStates.notInitiated}
                placeholder="Enter 10-digit mobile number"
                error={!!phoneError}
                helperText={phoneError}
                inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                }}
            />

            {/* OTP Field */}
            {showOtpField && (
                <TextField
                    label="Enter OTP"
                    variant="outlined"
                    fullWidth
                    value={otp}
                    onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Only digits
                        if (value.length <= 6) {
                            setOtp(value);
                        }
                    }}
                    margin="normal"
                    required
                    placeholder="Enter 6-digit OTP"
                    inputProps={{
                        maxLength: 6,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                    }}
                    autoFocus
                />
            )}

            {/* Incorrect OTP Message */}
            {otpState === OTPStates.incorrect && (
                <Alert severity="error" sx={{ mt: 1, width: "100%" }}>
                    Incorrect OTP. Please try again.
                </Alert>
            )}

            {/* Resend OTP */}
            {showOtpField && (
                <Box textAlign="center" mt={1} mb={2}>
                    {countdown > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Resend OTP in {countdown.toString().padStart(2, "0")} seconds
                        </Typography>
                    ) : (
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleResendOtp}
                            sx={{ cursor: "pointer" }}
                        >
                            Resend OTP
                        </Link>
                    )}
                </Box>
            )}

            {/* Action Buttons */}
            <Box display="flex" gap={2} width="100%" mt={2}>
                {/* Reset Button - only show when OTP field is visible */}
                {showOtpField && (
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        disabled={isLoading}
                        sx={{ minWidth: 100 }}
                    >
                        Reset
                    </Button>
                )}

                {/* Main Action Button */}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={showOtpField ? handleVerifyOtp : handleSendOtp}
                    disabled={
                        isLoading ||
                        (otpState === OTPStates.notInitiated && phone.length !== 10) ||
                        (showOtpField && otp.length !== 6)
                    }
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? "Processing..." : showOtpField ? "Verify OTP" : "Send OTP"}
                </Button>
            </Box>

            {/* Failed State */}
            {otpState === OTPStates.failed && (
                <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                    <Typography variant="body2" fontWeight="bold">
                        Something went wrong
                    </Typography>
                    <Typography variant="body2">
                        We failed to send OTP. Please try again.
                    </Typography>
                </Alert>
            )}

            {/* Invisible Recaptcha Container */}
            <div id="recaptcha-container" style={{ marginTop: 16 }}></div>
        </Box>
    );
};

export default Login;
